import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import {
    getMagazine,
    toggleMagazineLike,
    createMagazineComment,
    getMagazineCommentList,
    translateMagazine,
    translateMagazineComment,
    IMagazineResponse,
    IMagazineCommentListResponse
} from "api/student/Magazine.api";
import { formatTimeAgo } from "helpers/Datetime.helper";
import { getImageUrl } from "helpers/Image.helper";
import * as InAppHelper from "helpers/InApp.helper";
import { IBbs, IComment } from "api/interfaces/bbs.interface";
import { IPagination } from "api/interfaces/common.interface";


const MagazineDetail = () => {
    const { pathname } = useLocation();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [magazine, setMagazine] = useState<IBbs | null>(null);
    const [comments, setComments] = useState<IComment[]>([]);
    const [pagination, setPagination] = useState<IPagination | null>(null);
    const [loading, setLoading] = useState(true);
    const [commentLoading, setCommentLoading] = useState(false);
    const [newComment, setNewComment] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [showComments, setShowComments] = useState(false);
    const [isSubmittingComment, setIsSubmittingComment] = useState(false);
    const [isLoadingMoreComments, setIsLoadingMoreComments] = useState(false);
    const [translatedContents, setTranslatedContents] = useState<{ [key: string]: { title?: string, text: string } }>({});
    const [isTranslating, setIsTranslating] = useState<{ [key: string]: boolean }>({});
    const [translatedComments, setTranslatedComments] = useState<{ [key: string]: string }>({});
    const [isTranslatingComment, setIsTranslatingComment] = useState<{ [key: string]: boolean }>({});
    const commentScrollRef = useRef<HTMLDivElement>(null);
    const [menuPb, setMenuPb] = useState('pb-safe');
    const [isInputFocused, setIsInputFocused] = useState(false);

    const updateMenuPb = async () => {
        if (isInputFocused) {
            setMenuPb("pb-safe");
        } else {
            const isIos = await InAppHelper.getIsIos();
            if (!isIos) {
                setMenuPb("pb-8");
            }
        }
    }

    useEffect(() => {
        updateMenuPb();
    }, [pathname, isInputFocused]);

    useEffect(() => {
        if (id) {
            loadMagazineDetail();
            loadComments();
        }
    }, [id]);

    const handleLoadMoreComments = useCallback(async () => {
        if (isLoadingMoreComments || !pagination?.hasNextPage) return;

        try {
            setIsLoadingMoreComments(true);
            const nextPage = currentPage + 1;
            const response = await getMagazineCommentList(Number(id), { page: nextPage });
            const data: IMagazineCommentListResponse = response;

            setComments(prev => [...prev, ...data.commentList]);
            setCurrentPage(nextPage);
            setPagination(data.pagination);
        } catch (error) {
            console.error("추가 댓글 로드 실패:", error);
        } finally {
            setIsLoadingMoreComments(false);
        }
    }, [currentPage, pagination?.hasNextPage, isLoadingMoreComments, id]);

    useEffect(() => {
        if (currentPage > 1) {
            loadComments();
        }
    }, [currentPage]);

    // 무한 스크롤 이벤트 리스너
    useEffect(() => {
        if (!showComments || !pagination?.hasNextPage || !commentScrollRef.current) return;

        let isScrolling = false;
        let scrollTimeout: NodeJS.Timeout;

        const handleScroll = () => {
            if (isScrolling || isLoadingMoreComments || !commentScrollRef.current) return;

            const scrollTop = commentScrollRef.current.scrollTop;
            const scrollHeight = commentScrollRef.current.scrollHeight;
            const clientHeight = commentScrollRef.current.clientHeight;
            const scrollPosition = scrollTop + clientHeight;
            const triggerPosition = scrollHeight - 50; // 50px 여유

            if (scrollPosition >= triggerPosition) {
                isScrolling = true;
                handleLoadMoreComments().finally(() => {
                    clearTimeout(scrollTimeout);
                    scrollTimeout = setTimeout(() => {
                        isScrolling = false;
                    }, 1000);
                });
            }
        };

        const commentContainer = commentScrollRef.current;
        commentContainer.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            commentContainer.removeEventListener('scroll', handleScroll);
            clearTimeout(scrollTimeout);
        };
    }, [showComments, pagination?.hasNextPage, isLoadingMoreComments, handleLoadMoreComments]);

    const loadMagazineDetail = async () => {
        try {
            setLoading(true);
            const response = await getMagazine(Number(id));
            setMagazine(response.magazine);
        } catch (error) {
            console.error("매거진 상세 로드 실패:", error);
        } finally {
            setLoading(false);
        }
    };

    const loadComments = async () => {
        try {
            setCommentLoading(true);
            const response = await getMagazineCommentList(Number(id), { page: currentPage });
            const data: IMagazineCommentListResponse = response;

            if (currentPage === 1) {
                setComments(data.commentList);
            } else {
                setComments(prev => [...prev, ...data.commentList]);
            }
            setPagination(data.pagination);
        } catch (error) {
            console.error("댓글 목록 로드 실패:", error);
        } finally {
            setCommentLoading(false);
        }
    };

    const handleLikeToggle = async () => {
        if (!magazine) return;

        try {
            const response = await toggleMagazineLike(magazine.id);
            setMagazine(prev => prev ? {
                ...prev,
                isLiked: response.isLiked,
                likeCount: response.likeCount
            } : null);
        } catch (error) {
            console.error("좋아요 토글 실패:", error);
        }
    };

    const handleCommentSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newComment.trim() || !magazine || isSubmittingComment) return;

        try {
            setIsSubmittingComment(true);
            const response = await createMagazineComment(magazine.id, newComment.trim());
            setComments(prev => [response.comment, ...prev]);
            setNewComment("");

            // 댓글 수 업데이트
            setMagazine(prev => prev ? {
                ...prev,
                commentCount: prev.commentCount + 1
            } : null);
        } catch (error) {
            console.error("댓글 작성 실패:", error);
        } finally {
            setIsSubmittingComment(false);
        }
    };

    // 번역 함수
    const handleTranslate = async (cardId: string, isMainCard: boolean = false) => {
        try {
            setIsTranslating(prev => ({ ...prev, [cardId]: true }));

            if (isMainCard && magazine) {
                // 메인 카드 번역
                const response = await translateMagazine(magazine.id);
                setTranslatedContents(prev => ({
                    ...prev,
                    [cardId]: {
                        title: response.translatedTitle,
                        text: response.translatedText
                    }
                }));
            } else if (!isMainCard && magazine?.childList) {
                // 자식 카드 번역
                const childIndex = parseInt(cardId) - 2; // 카드 번호에서 인덱스 계산
                const child = magazine.childList[childIndex];
                if (child) {
                    const response = await translateMagazine(child.id);
                    setTranslatedContents(prev => ({
                        ...prev,
                        [cardId]: {
                            title: response.translatedTitle,
                            text: response.translatedText
                        }
                    }));
                }
            }
        } catch (error) {
            console.error("번역 실패:", error);
            alert("번역에 실패했습니다. 다시 시도해주세요.");
        } finally {
            setIsTranslating(prev => ({ ...prev, [cardId]: false }));
        }
    };

    // 코멘트 번역 함수
    const handleCommentTranslate = async (commentId: number) => {
        if (!magazine) return;

        try {
            setIsTranslatingComment(prev => ({ ...prev, [commentId]: true }));

            const response = await translateMagazineComment(magazine.id, commentId);
            setTranslatedComments(prev => ({
                ...prev,
                [commentId]: response.translatedText
            }));
        } catch (error) {
            console.error("코멘트 번역 실패:", error);
            alert("번역에 실패했습니다. 다시 시도해주세요.");
        } finally {
            setIsTranslatingComment(prev => ({ ...prev, [commentId]: false }));
        }
    };




    // 스켈레톤 컴포넌트
    const SkeletonDetail = () => (
        <main className="bg-gray-50 min-h-screen overflow-y-auto animate-pulse">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6">
                <div className="max-w-4xl mx-auto">
                    {/* 헤더 영역 스켈레톤 */}
                    <div className="mb-6">
                        {/* 뒤로가기 버튼 스켈레톤 */}
                        <div className="mb-4">
                            <div className="w-6 h-6 bg-gray-300 rounded"></div>
                        </div>

                        {/* 작성자 정보 스켈레톤 */}
                        <div className="flex items-start gap-3">
                            {/* 프로필 사진 스켈레톤 */}
                            <div className="w-10 h-10 bg-gray-300 rounded-full flex-shrink-0"></div>

                            {/* 닉네임과 날짜 스켈레톤 */}
                            <div className="flex-1">
                                <div className="h-4 bg-gray-300 rounded w-24 mb-2"></div>
                                <div className="h-3 bg-gray-300 rounded w-16"></div>
                            </div>
                        </div>
                    </div>

                    {/* 매거진 카드 스켈레톤 */}
                    <div className="pb-4">
                        <div className="flex gap-4">
                            {/* 본문 카드 스켈레톤 */}
                            <div className="bg-white rounded-xl shadow-sm overflow-hidden flex-shrink-0 w-full">
                                {/* 카드 헤더 스켈레톤 */}
                                <div className="bg-gray-300 px-3 py-1 flex items-center justify-between">
                                    <div className="h-3 bg-gray-400 rounded w-4"></div>
                                    <div className="h-5 w-5 bg-gray-400 rounded"></div>
                                </div>

                                {/* 카드 내용 스켈레톤 */}
                                <div className="px-6 py-6">
                                    {/* 제목 스켈레톤 */}
                                    <div className="mb-4">
                                        <div className="h-6 bg-gray-300 rounded w-3/4"></div>
                                    </div>

                                    {/* 내용 스켈레톤 */}
                                    <div className="space-y-2 mb-4">
                                        <div className="h-4 bg-gray-300 rounded w-full"></div>
                                        <div className="h-4 bg-gray-300 rounded w-full"></div>
                                        <div className="h-4 bg-gray-300 rounded w-2/3"></div>
                                        <div className="h-4 bg-gray-300 rounded w-4/5"></div>
                                    </div>

                                    {/* 이미지 스켈레톤 */}
                                    <div className="mt-4 space-y-3">
                                        <div className="h-48 bg-gray-300 rounded-lg"></div>
                                        <div className="h-32 bg-gray-300 rounded-lg"></div>
                                    </div>
                                </div>
                            </div>

                            {/* 자식 카드들 스켈레톤 (2개) */}
                            {Array.from({ length: 2 }).map((_, index) => (
                                <div key={index} className="bg-white rounded-xl shadow-sm overflow-hidden flex-shrink-0" style={{ width: 'calc(90vw - 2rem)' }}>
                                    {/* 카드 헤더 스켈레톤 */}
                                    <div className="bg-gray-300 px-3 py-1 flex items-center justify-between">
                                        <div className="h-3 bg-gray-400 rounded w-4"></div>
                                        <div className="h-5 w-5 bg-gray-400 rounded"></div>
                                    </div>

                                    {/* 카드 내용 스켈레톤 */}
                                    <div className="px-6 py-6">
                                        {/* 제목 스켈레톤 */}
                                        <div className="mb-4">
                                            <div className="h-6 bg-gray-300 rounded w-2/3"></div>
                                        </div>

                                        {/* 내용 스켈레톤 */}
                                        <div className="space-y-2 mb-4">
                                            <div className="h-4 bg-gray-300 rounded w-full"></div>
                                            <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                                            <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                                        </div>

                                        {/* 이미지 스켈레톤 */}
                                        <div className="mt-4">
                                            <div className="h-32 bg-gray-300 rounded-lg"></div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* 하단 스티키 영역 스켈레톤 */}
            <div className="fixed bottom-0 left-0 right-0 z-50">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 mb-4">
                    <div className="max-w-4xl mx-auto">
                        <div className="bg-white/90 backdrop-blur-md border-t border-gray-200 shadow-lg rounded-b-xl">
                            {/* 좋아요/댓글 영역 스켈레톤 */}
                            <div className="flex items-center justify-between px-3 pt-2 border-b border-gray-100">
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-1 px-2 py-1">
                                        <div className="h-4 w-4 bg-gray-300 rounded"></div>
                                        <div className="h-3 bg-gray-300 rounded w-6"></div>
                                    </div>
                                    <div className="flex items-center gap-1 px-2 py-1">
                                        <div className="h-4 w-4 bg-gray-300 rounded"></div>
                                        <div className="h-3 bg-gray-300 rounded w-6"></div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 px-2 py-1">
                                    <div className="h-3 bg-gray-300 rounded w-16"></div>
                                    <div className="h-3 w-3 bg-gray-300 rounded"></div>
                                </div>
                            </div>

                            {/* 댓글 입력 영역 스켈레톤 */}
                            <div className="px-4 py-3">
                                <div className="flex gap-3">
                                    <div className="flex-1 h-10 bg-gray-300 rounded-lg"></div>
                                    <div className="h-10 w-16 bg-gray-300 rounded-lg"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );

    const SkeletonComment = () => (
        <div className="bg-white rounded-lg shadow-sm p-4 animate-pulse">
            <div className="flex gap-3">
                <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                <div className="flex-1">
                    <div className="bg-gray-200 rounded h-4 w-24 mb-2"></div>
                    <div className="bg-gray-200 rounded h-4 w-full mb-1"></div>
                    <div className="bg-gray-200 rounded h-4 w-3/4"></div>
                </div>
            </div>
        </div>
    );

    if (loading) {
        return <SkeletonDetail />;
    }

    if (!magazine) {
        return (
            <main className="bg-gray-50 min-h-screen overflow-y-auto">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6">
                    <div className="max-w-4xl mx-auto">
                        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                            <div className="p-6 text-center">
                                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtContentNotFound")}
                                </h3>
                                <p className="text-gray-500 mb-6">
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtContentNotFoundDesc")}
                                </p>
                                <button
                                    onClick={() => navigate("/student/hub/magazine")}
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#CF3688] hover:bg-[#B8327A] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#CF3688]"
                                >
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                    </svg>
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtBackToList")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        );
    }

    return (
        <main className="bg-gray-50 min-h-screen overflow-y-auto">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6">
                <div className="max-w-4xl mx-auto">
                    {/* 헤더 영역 */}
                    <div className="mb-6">
                        {/* 뒤로가기 버튼 */}
                        <div className="mb-4">
                            <button
                                onClick={() => navigate("/student/hub/magazine")}
                                className="inline-flex items-center text-gray-500 hover:text-gray-700 transition-colors"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                        </div>

                        {/* 작성자 및 작성일시 */}
                        <div className="flex items-start gap-3">
                            {/* 프로필 사진 */}
                            <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                                {magazine.user?.userStudent?.profileImage ? (
                                    <img
                                        src={getImageUrl(magazine.user.userStudent.profileImage)}
                                        alt="프로필"
                                        className="w-full h-full rounded-full object-cover"
                                        onError={(e) => {
                                            e.currentTarget.style.display = 'none';
                                            e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                        }}
                                    />
                                ) : null}
                                <div className={`w-full h-full rounded-full bg-gray-300 flex items-center justify-center text-gray-600 font-medium ${magazine.user?.userStudent?.profileImage ? 'hidden' : ''}`}>
                                    <span className="text-sm">
                                        {(magazine.user?.userStudent?.nickname || magazine.user?.userStudent?.firstName || "익").charAt(0)}
                                    </span>
                                </div>
                            </div>

                            {/* 닉네임과 날짜 */}
                            <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900">
                                    {magazine.user?.userStudent?.nickname || magazine.user?.userStudent?.firstName || "익명"}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                    {formatTimeAgo(magazine.createdAt)}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 매거진 스레드 캐러셀 */}
                    <div className={`pb-4 ${magazine.childList && magazine.childList.length > 0 ? 'overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100' : ''}`}>
                        <div className={`flex gap-4 ${magazine.childList && magazine.childList.length > 0 ? '' : 'w-full'}`} style={{ width: magazine.childList && magazine.childList.length > 0 ? 'max-content' : '100%' }}>
                            {/* 본문 글 */}
                            <div
                                className="bg-white rounded-xl shadow-sm overflow-hidden flex-shrink-0"
                                style={{
                                    width: magazine.childList && magazine.childList.length > 0
                                        ? 'calc(90vw - 2rem)'
                                        : '100%'
                                }}
                            >
                                {/* 카드 헤더 */}
                                <div className="bg-[#CF3688] text-white px-3 py-1 flex items-center justify-between">
                                    <span className="text-xs font-medium">1</span>
                                    <button
                                        onClick={() => handleTranslate("1", true)}
                                        disabled={isTranslating["1"]}
                                        className="p-1 bg-black hover:bg-gray-800 rounded transition-colors disabled:opacity-50"
                                        title="번역"
                                    >
                                        {isTranslating["1"] ? (
                                            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        ) : (
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                                {/* 내용 */}
                                <div className="px-6 py-6">
                                    {/* 제목 */}
                                    <div className="mb-4">
                                        <h1 className="text-lg font-bold text-gray-900 leading-tight">
                                            {magazine.title || magazine.subject}
                                        </h1>
                                        {translatedContents["1"]?.title && (
                                            <div className="mt-2 p-3 bg-gray-100 border-l-4 border-gray-400 rounded-r">
                                                <div className="text-lg font-bold text-gray-900 leading-tight">
                                                    {translatedContents["1"].title}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="prose prose-gray max-w-none">
                                        <div className="text-gray-700 leading-relaxed whitespace-pre-wrap text-sm">
                                            {magazine.text}
                                        </div>
                                        {translatedContents["1"]?.text && (
                                            <div className="mt-3 p-3 bg-gray-100 border-l-4 border-gray-400 rounded-r">
                                                <div className="text-gray-700 leading-relaxed whitespace-pre-wrap text-sm">
                                                    {translatedContents["1"].text}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* 이미지 */}
                                    {magazine.imageList && magazine.imageList.length > 0 && (
                                        <div className="mt-4">
                                            <div className="space-y-3">
                                                {magazine.imageList.map((imageItem, index) => (
                                                    <div key={imageItem.id} className="w-full">
                                                        <div className="w-full rounded-lg overflow-hidden bg-gray-100 border border-gray-300">
                                                            <img
                                                                src={getImageUrl(imageItem.image)}
                                                                alt={`${magazine.title || magazine.subject} 이미지 ${index + 1}`}
                                                                className="w-full h-auto object-cover"
                                                                onError={(e) => {
                                                                    e.currentTarget.style.display = 'none';
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                            </div>

                            {/* 자식글들 */}
                            {magazine.childList && magazine.childList.map((child, index) => (
                                <div
                                    key={child.id}
                                    className="bg-white rounded-xl shadow-sm overflow-hidden flex-shrink-0"
                                    style={{ width: 'calc(90vw - 2rem)' }}
                                >
                                    {/* 카드 헤더 */}
                                    <div className="bg-[#CF3688] text-white px-3 py-1 flex items-center justify-between">
                                        <span className="text-xs font-medium">{index + 2}</span>
                                        <button
                                            onClick={() => handleTranslate(`${index + 2}`, false)}
                                            disabled={isTranslating[`${index + 2}`]}
                                            className="p-1 bg-black hover:bg-gray-800 rounded transition-colors disabled:opacity-50"
                                            title="번역"
                                        >
                                            {isTranslating[`${index + 2}`] ? (
                                                <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                            ) : (
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                    {/* 내용 */}
                                    <div className="px-6 py-6">
                                        {/* 제목 */}
                                        {child.title && (
                                            <div className="mb-4">
                                                <h2 className="text-lg font-bold text-gray-900 leading-tight">
                                                    {child.title}
                                                </h2>
                                                {translatedContents[`${index + 2}`]?.title && (
                                                    <div className="mt-2 p-3 bg-gray-100 border-l-4 border-gray-400 rounded-r">
                                                        <div className="text-lg font-bold text-gray-900 leading-tight">
                                                            {translatedContents[`${index + 2}`].title}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <div className="prose prose-gray max-w-none">
                                            <div className="text-gray-700 leading-relaxed whitespace-pre-wrap text-sm">
                                                {child.text}
                                            </div>
                                            {translatedContents[`${index + 2}`]?.text && (
                                                <div className="mt-3 p-3 bg-gray-100 border-l-4 border-gray-400 rounded-r">
                                                    <div className="text-gray-700 leading-relaxed whitespace-pre-wrap text-sm">
                                                        {translatedContents[`${index + 2}`].text}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* 이미지 */}
                                        {child.imageList && child.imageList.length > 0 && (
                                            <div className="mt-4">
                                                <div className="space-y-3">
                                                    {child.imageList.map((imageItem, imageIndex) => (
                                                        <div key={imageItem.id} className="w-full">
                                                            <div className="w-full rounded-lg overflow-hidden bg-gray-100 border border-gray-300">
                                                                <img
                                                                    src={getImageUrl(imageItem.image)}
                                                                    alt={`${child.title || child.subject} 이미지 ${imageIndex + 1}`}
                                                                    className="w-full h-auto object-cover"
                                                                    onError={(e) => {
                                                                        e.currentTarget.style.display = 'none';
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                </div>
                            ))}
                        </div>
                    </div>

                    {/* 댓글 작성 - 스티키 */}
                    <div
                        className={`fixed bottom-0 left-0 right-0 z-50 transition-all duration-300 ease-in-out ${menuPb}`}
                        style={{
                            // menuPb 값에 따라 paddingBottom 등 스타일이 변할 수 있다면 여기에 추가
                        }}
                    >
                        {/* 댓글 목록 - 스티키 영역 내부 */}
                        <div className="container mx-auto px-4 sm:px-6 lg:px-8 mb-4">
                            <div className="max-w-4xl mx-auto">
                                {/* 댓글 목록 */}
                                {showComments && (
                                    <div ref={commentScrollRef} className="max-h-96 overflow-y-auto bg-white/80 backdrop-blur-md rounded-t-xl p-4">
                                        {commentLoading && comments.length === 0 ? (
                                            <div>
                                                {[...Array(3)].map((_, index) => (
                                                    <SkeletonComment key={index} />
                                                ))}
                                            </div>
                                        ) : (
                                            <>
                                                {comments.map((comment) => (
                                                    <div key={comment.id} className="bg-white/60 backdrop-blur-sm shadow-sm">
                                                        <div className="px-4 py-3">
                                                            <div className="flex gap-3">
                                                                <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                                                                    {comment.user?.userStudent?.profileImage ? (
                                                                        <img
                                                                            src={getImageUrl(comment.user.userStudent.profileImage)}
                                                                            alt="프로필"
                                                                            className="w-full h-full object-cover"
                                                                            onError={(e) => {
                                                                                e.currentTarget.style.display = 'none';
                                                                                e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                                                            }}
                                                                        />
                                                                    ) : null}
                                                                    <span className={`text-sm font-medium text-gray-600 ${comment.user?.userStudent?.profileImage ? 'hidden' : ''}`}>
                                                                        {(comment.user?.userStudent?.nickname || comment.user?.userStudent?.firstName || "익").charAt(0)}
                                                                    </span>
                                                                </div>
                                                                <div className="flex-1">
                                                                    <div className="mb-1 flex items-center justify-between">
                                                                        <div>
                                                                            <div className="text-xs font-medium text-gray-900">
                                                                                {comment.user?.userStudent?.nickname || comment.user?.userStudent?.firstName || "익명"}
                                                                            </div>
                                                                            <div className="text-[10px] text-gray-400">
                                                                                {formatTimeAgo(comment.createdAt)}
                                                                            </div>
                                                                        </div>
                                                                        <button
                                                                            onClick={() => handleCommentTranslate(comment.id)}
                                                                            disabled={isTranslatingComment[comment.id]}
                                                                            className="p-1 border border-black hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                                                                            title="번역"
                                                                        >
                                                                            {isTranslatingComment[comment.id] ? (
                                                                                <svg className="w-3 h-3 animate-spin text-black" fill="none" viewBox="0 0 24 24">
                                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                                </svg>
                                                                            ) : (
                                                                                <svg className="w-3 h-3 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                                                                </svg>
                                                                            )}
                                                                        </button>
                                                                    </div>
                                                                    <p className="text-sm text-gray-700">{comment.text}</p>
                                                                    {translatedComments[comment.id] && (
                                                                        <div className="mt-2 p-2 bg-gray-100 border-l-4 border-gray-400 rounded-r">
                                                                            <div className="text-sm text-gray-700">
                                                                                {translatedComments[comment.id]}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}

                                                {/* 무한 스크롤 로딩 인디케이터 */}
                                                {isLoadingMoreComments && (
                                                    <div className="flex justify-center items-center py-4">
                                                        <div className="flex items-center space-x-2">
                                                            <svg className="w-4 h-4 animate-spin text-[#CF3688]" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            <span className="text-xs text-gray-500">더 많은 댓글을 불러오는 중...</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}

                                {/* 액션 버튼과 댓글 입력 */}
                                <div className="bg-white/90 backdrop-blur-md border-t border-gray-200 shadow-lg rounded-b-xl">
                                    {/* 좋아요/댓글 액션 버튼 */}
                                    <div className="flex items-center justify-between px-3 pt-2 border-b border-gray-100">
                                        <div className="flex items-center gap-4">
                                            <button
                                                onClick={handleLikeToggle}
                                                className={`flex items-center gap-1 px-2 py-1 rounded-lg transition-colors ${magazine?.isLiked
                                                    ? "bg-red-50 text-red-600"
                                                    : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                                                    }`}
                                            >
                                                <svg className="w-4 h-4" fill={magazine?.isLiked ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                                </svg>
                                                <span className="text-xs font-medium">{magazine?.likeCount || 0}</span>
                                            </button>

                                            <div className="flex items-center gap-1 px-2 py-1 text-gray-600">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                                </svg>
                                                <span className="text-xs font-medium">{magazine?.commentCount || 0}</span>
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => setShowComments(!showComments)}
                                            className="flex items-center gap-1 px-2 py-1 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                                        >
                                            <span className="text-xs font-medium">
                                                {showComments ? getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtCommentHide") : getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtCommentShow")}
                                            </span>
                                            <svg
                                                className={`w-3 h-3 transition-transform ${showComments ? 'rotate-180' : ''}`}
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={"M7 14l5-5 5 5"} />
                                            </svg>
                                        </button>
                                    </div>

                                    {/* 댓글 입력 */}
                                    <div className="px-4 py-3">
                                        <form onSubmit={handleCommentSubmit} className="flex gap-3">
                                            <input
                                                type="text"
                                                value={newComment}
                                                onChange={(e) => setNewComment(e.target.value)}
                                                onFocus={() => setIsInputFocused(true)}
                                                onBlur={() => setIsInputFocused(false)}
                                                placeholder={getLanguageValue(PAGE_TYPE.STUDENT, "hubCommunity.txtCommentInputPlaceholder")}
                                                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#CF3688] focus:border-transparent"
                                            />
                                            <button
                                                type="submit"
                                                disabled={!newComment.trim() || isSubmittingComment}
                                                className="px-3 py-2 text-sm bg-[#CF3688] text-white rounded-lg hover:bg-[#B8327A] disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {getLanguageValue(PAGE_TYPE.STUDENT, "hubCommunity.btnWrite")}
                                            </button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default MagazineDetail;
